import { getAuthHeaders } from "@/lib/auth-fetch";
import { API_BASE } from "@/lib/utils";

/**
 * FormSG → Plumber Custom API mapping (Kenneth / JTC live pipe shape).
 * Keep in sync with backend integrations.gov_mode.ALLOWED_FORMSG_FIELDS.
 */
export const PLUMBER_FIELD_MAPPING = [
  {
    formsg: "FSGID",
    sendToAivory: true,
    required: true,
    aivory: "formsg.submissionId",
    note: "Required — unique submission id (not the form id)",
    plumberKey: null, // nested under formsg.submissionId
  },
  {
    formsg: "AttendanceType",
    sendToAivory: true,
    required: true,
    aivory: "ticketTier",
    note: "Map into ticketTier (e.g. General)",
    plumberKey: "ticketTier",
  },
  {
    formsg: "Name",
    sendToAivory: true,
    required: false,
    aivory: "Name",
    note: "Optional — display label",
    plumberKey: "Name",
  },
  {
    formsg: "Designation",
    sendToAivory: true,
    required: false,
    aivory: "Designation",
    note: "Optional — display label",
    plumberKey: "Designation",
  },
  {
    formsg: "CompanyName",
    sendToAivory: true,
    required: false,
    aivory: "CompanyName",
    note: "Optional — display label",
    plumberKey: "CompanyName",
  },
  {
    formsg: "Email",
    sendToAivory: true,
    required: false,
    aivory: "Email",
    note: "Optional — stored as SHA-256 hash only",
    plumberKey: "Email",
  },
];

/** Default selected FormSG fields for the Plumber JSON builder. */
export const DEFAULT_SELECTED_FORMSG_FIELDS = PLUMBER_FIELD_MAPPING
  .filter((row) => row.sendToAivory)
  .map((row) => row.formsg);

export async function fetchGovWorkspaceSettings() {
  const res = await fetch(`${API_BASE}/api/admin/gov/settings`, { headers: getAuthHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof body?.detail === "string" ? body.detail : "Failed to load gov settings");
  return {
    gov: body.gov || {},
    entitled: body.entitled === true,
    plan: body.plan || "studio",
    canRequestAccess: body.canRequestAccess !== false,
  };
}

export async function requestGovAccess({ contactEmail = "", notes = "" } = {}) {
  const res = await fetch(`${API_BASE}/api/admin/gov/request-access`, {
    method: "POST",
    headers: { "content-type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify({ contactEmail, notes }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof body?.detail === "string" ? body.detail : "Failed to submit request");
  return body;
}

export async function saveGovWorkspaceSettings(gov) {
  const res = await fetch(`${API_BASE}/api/admin/gov/settings`, {
    method: "PUT",
    headers: { "content-type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify(gov),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.detail || "Failed to save gov settings");
  return body.gov;
}

export async function fetchEventGovIntegration(eventId) {
  const res = await fetch(`${API_BASE}/api/admin/events/${encodeURIComponent(eventId)}/gov-integration`, {
    headers: getAuthHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof body?.detail === "string" ? body.detail : "Failed to load gov integration");
  return {
    config: body.config || null,
    entitled: body.entitled === true,
    plan: body.plan || "studio",
    canRequestAccess: body.canRequestAccess !== false,
  };
}

export async function saveEventGovIntegration(eventId, payload) {
  const res = await fetch(`${API_BASE}/api/admin/events/${encodeURIComponent(eventId)}/gov-integration`, {
    method: "PUT",
    headers: { "content-type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.detail || "Failed to save gov integration");
  return body;
}

export async function loadGovAttendees(eventId, { q = "", limit = 500, offset = 0, signal } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (String(q || "").trim()) params.set("q", String(q).trim());
  const res = await fetch(
    `${API_BASE}/api/admin/events/${encodeURIComponent(eventId)}/gov-attendees?${params}`,
    { headers: getAuthHeaders(), signal },
  );
  const body = await res.json().catch(() => ({}));
  if (!res.ok) return { attendees: [], total: 0 };
  return { attendees: body.attendees || [], total: body.total || 0 };
}

/**
 * Live Plumber Custom API "Data" JSON — matches Kenneth's working pipe shape.
 * `selectedFields` is a list of FormSG field names (from PLUMBER_FIELD_MAPPING).
 * Toggling optional fields (Name, Designation, …) adds/removes them here.
 */
export function plumberIngestExample(
  eventId,
  {
    formId = "",
    ingestSecret = "",
    submissionId = "{{FSGID}}",
    ticketTier = "{{AttendanceType}}",
    selectedFields = DEFAULT_SELECTED_FORMSG_FIELDS,
  } = {},
) {
  const selected = new Set(
    (Array.isArray(selectedFields) ? selectedFields : DEFAULT_SELECTED_FORMSG_FIELDS).map(String),
  );
  // Required keys always present
  selected.add("FSGID");
  selected.add("AttendanceType");

  const body = {
    ingestSecret: ingestSecret || "<paste ingest secret here>",
    schema: "aivory.gov.ingest.v1",
    eventId,
    formsg: {
      formId: formId || "<form id>",
      submissionId: selected.has("FSGID") ? submissionId : undefined,
    },
    ticketTier: selected.has("AttendanceType") ? ticketTier : undefined,
  };

  for (const row of PLUMBER_FIELD_MAPPING) {
    if (!row.plumberKey || row.required) continue;
    if (!selected.has(row.formsg)) continue;
    body[row.plumberKey] = `{{${row.formsg}}}`;
  }

  // Drop undefined keys for a clean paste-ready payload
  if (body.ticketTier === undefined) delete body.ticketTier;
  if (body.formsg?.submissionId === undefined) delete body.formsg.submissionId;
  return body;
}

/** Plain-text mapping for clipboard / Plumber notes. */
export function plumberMappingGuideText({
  eventId = "evt_…",
  formId = "",
  ingestSecret = "",
  selectedFields = DEFAULT_SELECTED_FORMSG_FIELDS,
} = {}) {
  const secret = ingestSecret || "<ingest secret from Event → Government>";
  const fid = formId || "<form.gov.sg id>";
  const example = plumberIngestExample(eventId, {
    formId: fid,
    ingestSecret: secret,
    selectedFields,
  });
  const selected = new Set((selectedFields || []).map(String));
  selected.add("FSGID");
  selected.add("AttendanceType");
  const optional = PLUMBER_FIELD_MAPPING
    .filter((r) => !r.required && selected.has(r.formsg))
    .map((r) => r.formsg);

  const lines = [
    "AivoryEMS — Plumber Custom API (Data)",
    "",
    "Paste this JSON into Plumber → Custom API → Data.",
    "Replace {{…}} variables with FormSG fields:",
    "  formsg.submissionId  ← FormSG FSGID (unique per submission — NOT the form id)",
    "  ticketTier           ← FormSG AttendanceType (e.g. General)",
  ];
  if (optional.length) {
    lines.push(`  Optional included: ${optional.join(", ")}`);
  }
  lines.push(
    "",
    "Any other field → HTTP 422.",
    "",
    "Example:",
    JSON.stringify(example, null, 2),
    "",
    "Auth: ingestSecret in the JSON body (as above), OR connection header",
    "X-Aivory-Ingest-Secret / Authorization: Bearer <secret>.",
  );
  return lines.join("\n");
}
